import React, { JSX, lazy, Suspense } from "react";

import FallbackLoading from "./components/FallbackLoading";
import NotFound from "./components/NotFound";
import UserBolon from "./pages/user/bolon/bolon";
import UserKeyInFifo from "./pages/user/keyin/fifo/fifo";
import UserKeyIn from "./pages/user/keyin/keyin";
import UserKeyInStock from "./pages/user/keyin/stock/stock";
import UserKeyInVip from "./pages/user/keyin/vip/vip";
import Portal from "./pages/user/portal";
import ProfileSetting from "./pages/user/profile-settings";
import UserQCCallJob from "./pages/user/qc/call-jobs/call-jobs";
import UserQCCallJobFifo from "./pages/user/qc/call-jobs/fifo/fifo";
import UserQCCallJobStock from "./pages/user/qc/call-jobs/stock/stock";
import UserQCCallJobVip from "./pages/user/qc/call-jobs/vip/vip";
import UserQC from "./pages/user/qc/qc";
import UserSupport from "./pages/user/support/support";
import UserTaskCreator from "./pages/user/task-creator/task-creator";
import TaskInfo from "./pages/user/task-info";
import TaskList from "./pages/user/tasks";
import User from "./pages/user/user";
import YourTaskCreate from "./pages/user/your-task/create";
import YourTaskEdit from "./pages/user/your-task/edit";

const UserKeyInList = lazy(() => import("./pages/user/keyin/list"));
const UserKeyInYourTasks = lazy(() => import("./pages/user/keyin/your-tasks"));
const UserKeyInVipList = lazy(() => import("./pages/user/keyin/vip/list"));
const UserKeyInFifoList = lazy(() => import("./pages/user/keyin/fifo/list"));
const UserKeyInStockList = lazy(() => import("./pages/user/keyin/stock/list"));
const UserSupportList = lazy(() => import("./pages/user/support/list"));
const UserSupportInProgressList = lazy(
  () => import("./pages/user/support/in-progress-list"),
);
const UserSupportCompleteList = lazy(
  () => import("./pages/user/support/complete-list"),
);
const UserTaskCreatorList = lazy(
  () => import("./pages/user/task-creator/list"),
);
const UserTaskCreatorYourTaskList = lazy(
  () => import("./pages/user/task-creator/your-tasks"),
);
const UserTaskCreatorCreate = lazy(
  () => import("./pages/user/task-creator/create"),
);
const UserTaskCreatorEdit = lazy(
  () => import("./pages/user/task-creator/edit"),
);
const UserQCList = lazy(() => import("./pages/user/qc/list"));
const UserQCYourTasks = lazy(() => import("./pages/user/qc/your-tasks"));
const UserQCCallJobVipList = lazy(
  () => import("./pages/user/qc/call-jobs/vip/list"),
);
const UserQCCallJobFifoList = lazy(
  () => import("./pages/user/qc/call-jobs/fifo/list"),
);
const UserQCCallJobStockList = lazy(
  () => import("./pages/user/qc/call-jobs/stock/list"),
);

const WithSuspense = (
  Component: React.LazyExoticComponent<() => JSX.Element>,
) => (
  <Suspense fallback={<FallbackLoading />}>
    <Component />
  </Suspense>
);

export const userRoutes = [
  {
    path: "portal",
    element: <Portal />,
  },
  {
    path: "user",
    element: <User />,
    children: [
      {
        path: "profile-settings",
        element: <ProfileSetting />,
      },
      {
        path: "tasks",
        element: <TaskList />,
      },
      {
        path: "tasks/:id",
        element: <TaskInfo />,
      },
      {
        path: "your-tasks/create",
        element: <YourTaskCreate />,
      },
      {
        path: "your-tasks/edit/:id",
        element: <YourTaskEdit />,
      },
      {
        path: "task-creator",
        element: <UserTaskCreator />,
        children: [
          {
            path: "",
            element: WithSuspense(UserTaskCreatorList),
          },
          {
            path: "your-tasks",
            element: WithSuspense(UserTaskCreatorYourTaskList),
          },
          {
            path: "create",
            element: WithSuspense(UserTaskCreatorCreate),
          },
          {
            path: "edit/:id",
            element: WithSuspense(UserTaskCreatorEdit),
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
      {
        path: "keyin",
        element: <UserKeyIn />,
        children: [
          {
            path: "",
            element: WithSuspense(UserKeyInList),
          },
          {
            path: "your-tasks",
            element: WithSuspense(UserKeyInYourTasks),
          },
          {
            path: "vip",
            element: <UserKeyInVip />,
            children: [
              {
                path: "",
                element: WithSuspense(UserKeyInVipList),
              },
            ],
          },
          {
            path: "fifo",
            element: <UserKeyInFifo />,
            children: [
              {
                path: "",
                element: WithSuspense(UserKeyInFifoList),
              },
            ],
          },
          {
            path: "stock",
            element: <UserKeyInStock />,
            children: [
              {
                path: "",
                element: WithSuspense(UserKeyInStockList),
              },
            ],
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
      {
        path: "support",
        element: <UserSupport />,
        children: [
          {
            path: "",
            element: WithSuspense(UserSupportList),
          },
          {
            path: "in-progress",
            element: WithSuspense(UserSupportInProgressList),
          },
          {
            path: "completed",
            element: WithSuspense(UserSupportCompleteList),
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
      {
        path: "bolon",
        element: <UserBolon />,
      },
      {
        path: "qc",
        element: <UserQC />,
        children: [
          {
            path: "",
            element: WithSuspense(UserQCList),
          },
          {
            path: "your-tasks",
            element: WithSuspense(UserQCYourTasks),
          },
          {
            path: "call-jobs",
            element: <UserQCCallJob />,
            children: [
              {
                path: "vip",
                element: <UserQCCallJobVip />,
                children: [
                  {
                    path: "",
                    element: WithSuspense(UserQCCallJobVipList),
                  },
                ],
              },
              {
                path: "fifo",
                element: <UserQCCallJobFifo />,
                children: [
                  {
                    path: "",
                    element: WithSuspense(UserQCCallJobFifoList),
                  },
                ],
              },
              {
                path: "stock",
                element: <UserQCCallJobStock />,
                children: [
                  {
                    path: "",
                    element: WithSuspense(UserQCCallJobStockList),
                  },
                ],
              },
              {
                path: "*",
                element: <NotFound />,
              },
            ],
          },
          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default userRoutes;
